import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { tempTimeOptions } from '../../../../constants';
import { IRootState } from '../../../../redux/rootReducer';
import { generateTimeSlots } from '../../../../utils/utils';
import { SlotsProps } from '../BookAppointment';

export interface Availability {
    id: string;
    endDate: string;
    openTime: string;
    closeTime: string;
    startDate: string;
    locker: number;
}

export interface Appointment {
    endTime: string;
    startTime: string;
    reservedDate: string;
    availabilityId: string;
}

export interface Unit {
    id: string;
    name: string;
    price: number;
    rooms: number;
    baths: number;
    status: number;
    size: number;
    image_url: string;
    locationName: string;
    locationId: string;
    locationTimeZone: string;
    locationStreet1: string;
    locationStreet2: string;
    locationCity: string;
    locationState: string;
    locationZip: string;
    locationDescription: string;
    appointments?: Appointment[];
    availabilities: Availability[];
}

interface Props {
    unit: Unit;
    availabilityId: never[];
    timings: { startTime: moment.MomentInput; endTime: moment.MomentInput };
    setTimings: (timings: { startTime: string; endTime: string }) => void;
    selectedDate: string;
    setShowTime: (show: boolean) => void;
    setShowConfirm: (show: boolean) => void;
    setAvailableIds: React.Dispatch<React.SetStateAction<never[]>>;
    setSlots: React.Dispatch<React.SetStateAction<SlotsProps[]>>;
    setLockerInfo: React.Dispatch<React.SetStateAction<LockerInfo[]>>;
    lockerInfo: LockerInfo[];
}

export interface LockerInfo {
    id: string;
    locker: number;
    bookedStartTime: string;
    bookedCloseTime: string;
    rangeStartTime: string;
    rangeCloseTime: string;
    reservedDate: string;
}

interface TimeSlotRange {
    openTime: string;
    closeTime: string;
}

interface GroupedAvailability {
    date: string;
    timeSlots: TimeSlotRange[];
}

function groupAvailabilityByDate(data: Availability[]): GroupedAvailability[] {
    return Object.values(
        data.reduce<Record<string, GroupedAvailability>>((acc, item) => {
            const date = item.startDate;
            if (!acc[date]) {
                acc[date] = {
                    date,
                    timeSlots: [],
                };
            }
            acc[date].timeSlots.push({
                openTime: item.openTime,
                closeTime: item.closeTime,
            });
            return acc;
        }, {})
    );
}

const TimeSlots: React.FC<Props> = ({
    selectedDate,
    setSlots,
    setLockerInfo,
    lockerInfo,
}) => {
    const [bookedTime, setBookedTime] = useState<string[]>([]);
    const [reservedDates, setReservedDates] = useState<string[]>([]);

    const timeSlots = useSelector((state: IRootState) => state.unitReducer.unit.data.units);

    // Memoized locker info
    const memoizedLockerInfo = useMemo<LockerInfo[]>(() => {
        return (
            timeSlots?.appointments?.map((app: Appointment): LockerInfo | null => {
                const matchingAvailability = timeSlots.availabilities.find(
                    (avail: Availability) => avail.id === app.availabilityId
                );
                return matchingAvailability
                    ? {
                        id: matchingAvailability.id,
                        locker: matchingAvailability.locker,
                        bookedStartTime: app.startTime,
                        bookedCloseTime: app.endTime,
                        rangeStartTime: moment(app.startTime, 'HH:mm A').subtract(1, 'hour').format('HH:mm'),
                        rangeCloseTime: moment(app.endTime, 'HH:mm A').add(1, 'hour').format('HH:mm'),
                        reservedDate: app.reservedDate,
                    }
                    : null;
            }) || []
        ).filter((info: LockerInfo): info is LockerInfo => info !== null);
    }, [timeSlots]);

    useEffect(() => {
        setLockerInfo(memoizedLockerInfo);
    }, [memoizedLockerInfo, setLockerInfo]);


    function generateOneHourSlots(slots: TimeSlotRange[]): string[] {
        const sortedSlots = [...slots].sort((a, b) =>
            moment(a.openTime, 'HH:mm').diff(moment(b.openTime, 'HH:mm'))
        );

        const result: string[] = [];

        sortedSlots.forEach(({ openTime, closeTime }) => {
            let start = moment(openTime, 'HH:mm');
            const end = moment(closeTime, 'HH:mm');

            while (start.isBefore(end)) {
                const next = moment(start).add(1, 'hour');
                if (next.isAfter(end)) break;

                result.push(`${start.format('HH:mm')}-${next.format('HH:mm')}`);
                start = next;
            }
        });

        return result;
    }

    useEffect(() => {
        const grouped = groupAvailabilityByDate(timeSlots?.availabilities || []);
        const selectedDateFormatted = moment(selectedDate).format('YYYY-MM-DD');

        const dayAvailability = grouped.find((group) => group.date === selectedDateFormatted);
        const tempArray: SlotsProps[] = [];

        if (dayAvailability) {
            const oneHourRanges = generateOneHourSlots(dayAvailability.timeSlots);
            const now = moment(); // Local current time
            const isToday = moment(selectedDate).isSame(now, 'day'); // Check if selected date is today

            oneHourRanges.forEach((slotStr) => {
                const [startTime] = slotStr.split('-');

                const slotMoment = moment(
                    `${selectedDateFormatted} ${startTime}`,
                    'YYYY-MM-DD HH:mm'
                );

                const slotEndMoment = slotMoment.clone().add(1, 'hour');

                // Only skip the slot if it has fully passed
                if (isToday && slotEndMoment.isBefore(now)) return;

                const slotStartTime = slotMoment.format('YYYY-MM-DD HH:mm');

                const isBooked = timeSlots?.appointments?.some((app: Appointment) => {
                    const start = moment(
                        `${app.reservedDate} ${app.startTime}`,
                        'YYYY-MM-DD hh:mm a'
                    )
                        .subtract(1, 'hour')
                        .format('YYYY-MM-DD HH:mm');

                    const end = moment(
                        `${app.reservedDate} ${app.endTime}`,
                        'YYYY-MM-DD hh:mm a'
                    )
                        .add(1, 'hour')
                        .format('YYYY-MM-DD HH:mm');

                    return moment(slotStartTime).isBetween(start, end, undefined, '[)');
                });

                if (!isBooked) {
                    tempArray.push({
                        value: startTime,
                        label: startTime,
                    });
                }
            });
        }

        // Deduplicate and sort
        const uniqueSortedSlots = Array.from(
            new Map(tempArray.map((item) => [item.value, item])).values()
        ).sort((a, b) => a.value.localeCompare(b.value));

        setSlots(uniqueSortedSlots);
    }, [selectedDate, setSlots, timeSlots?.availabilities, timeSlots?.appointments]);


    useEffect(() => {
        const bookedTimes: string[] = [];
        const reserved: string[] = [];

        timeSlots?.appointments?.forEach((app: Appointment) => {
            if (
                moment(app.reservedDate).format('YYYY-MM-DD') ===
                moment(selectedDate).format('YYYY-MM-DD')
            ) {
                bookedTimes.push(app.startTime);
            }
            reserved.push(app.reservedDate);
        });

        setBookedTime(bookedTimes);
        setReservedDates(reserved);
    }, [timeSlots, selectedDate]);

    return <></>;
};

export default TimeSlots;
